import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

const ConfirmSignup: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { confirmSignUp, resendCode, signIn } = useAuth();

  const email = location.state?.email || '';
  const password = location.state?.password || '';
  const from = location.state?.from || '/';
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const confirmResult = await confirmSignUp(email, code);
      if (confirmResult.success) {
        // Auto-login after successful confirmation
        if (password) {
          const loginResult = await signIn(email, password);
          if (loginResult.success) {
            // Redirect to the page they were trying to access
            navigate(from, { replace: true });
          } else {
            // If auto-login fails, redirect to login page
            navigate('/auth/login', {
              state: { message: 'Account confirmed! Please sign in.' }
            });
          }
        } else {
          // No password available, redirect to login
          navigate('/auth/login', {
            state: { message: 'Account confirmed! Please sign in.' }
          });
        }
      } else {
        setError(confirmResult.error || 'Failed to confirm account');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setResendSuccess(false);
    setResending(true);

    try {
      const result = await resendCode(email);
      if (result.success) {
        setResendSuccess(true);
      } else {
        setError(result.error || 'Failed to resend code');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setResending(false);
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <p className="text-gray-400 mb-4">No email provided for confirmation.</p>
          <Link to="/auth/signup" className="text-blue-400 hover:text-blue-300">
            Go to Sign Up
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 rounded-full mb-4">
            <FontAwesomeIcon icon={solid("envelope")} className="text-3xl text-blue-500" />
          </div>
          <h2 className="text-4xl font-bold text-white mb-2">Check Your Email</h2>
          <p className="text-gray-400">
            We sent a verification code to<br />
            <span className="text-white font-medium">{email}</span>
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Success Message */}
        {resendSuccess && (
          <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded-lg flex items-center gap-2">
            <FontAwesomeIcon icon={solid("check-circle")} />
            Verification code resent successfully!
          </div>
        )}

        {/* Confirmation Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-300 mb-2">
              Verification Code
            </label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              maxLength={6}
              className="block w-full px-3 py-3 border border-gray-700 rounded-lg bg-gray-800 text-white text-center text-2xl tracking-widest placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="000000"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || code.length < 6}
            className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={solid("spinner")} className="animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify Account'
            )}
          </button>
        </form>

        {/* Resend Code */}
        <div className="text-center space-y-2">
          <p className="text-gray-400 text-sm">Didn't receive the code?</p>
          <button
            onClick={handleResendCode}
            disabled={resending}
            className="text-blue-400 hover:text-blue-300 font-medium transition-colors disabled:opacity-50"
          >
            {resending ? (
              <>
                <FontAwesomeIcon icon={solid("spinner")} className="animate-spin mr-2" />
                Sending...
              </>
            ) : (
              'Resend Code'
            )}
          </button>
        </div>

        {/* Back to Login */}
        <div className="text-center">
          <Link to="/auth/login" className="text-gray-400 hover:text-white text-sm transition-colors">
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ConfirmSignup;
